import React, { useState, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AddTerminalWizard from "../components/terminal/AddTerminalWizard";
import EditTerminalModal from "../components/terminal/EditTerminalModal";
import TerminalDetailsModal from "../components/terminal/TerminalDetailsModal";
import TerminalStatsCards from "../components/terminal/TerminalStatsCards";
import UnconfiguredTerminalsBanner from "../components/terminal/UnconfiguredTerminalsBanner";
import TerminalTableCard from "../components/terminal/TerminalTableCard";
import { Server, Copy, UserCheck } from "lucide-react";
import { zktecoAPI } from "../services/api";

const TerminalManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTerminals, setSelectedTerminals] = useState([]);

  // États pour l'édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // État pour l'auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  // États pour la vue détaillée
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTerminalDetails, setSelectedTerminalDetails] = useState(null);
  const [diag, setDiag] = useState(null);

  // État wizard d'ajout de terminal
  const [wizardStep, setWizardStep] = useState(1);

  // États pour la synchronisation biométrique
  const [syncingUserData, setSyncingUserData] = useState(false);
  const [syncType, setSyncType] = useState("all");
  const [showUserSyncModal, setShowUserSyncModal] = useState(false);
  const [selectedTerminalForSync, setSelectedTerminalForSync] = useState(null);

  // Statistics state

  // Form state for Add Terminal
  const [newTerminal, setNewTerminal] = useState({
    name: "",
    communication_mode: "push",
    terminal_type: "biometric",
    ip_address: "",
    port: 4370,
    serial_number: "",
    site_id: "",
    model: "",
    capabilities: [],
    firmware_version: "",
    max_users: null,
    max_logs: null,
  });

  // Connection test state
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);

  const [terminalsQ, sitesQ, typesQ, unconfiguredQ] = useQueries({
    queries: [
      {
        queryKey: ["terminals"],
        queryFn: () =>
          zktecoAPI.getTerminals({ limit: 1000 }).then((r) => r.data?.terminals || []),
        refetchInterval: autoRefresh ? 30000 : false,
      },
      {
        queryKey: ["terminal-sites"],
        queryFn: () => zktecoAPI.getSites().then((r) => r.data || []),
      },
      {
        queryKey: ["terminal-types"],
        queryFn: () => zktecoAPI.getTerminalTypes().then((r) => r.data || {}),
      },
      {
        queryKey: ["unconfigured-terminals"],
        queryFn: () =>
          zktecoAPI.getUnconfiguredTerminals().then((r) =>
            r.data?.success ? r.data.unconfigured_terminals || [] : []
          ),
      },
    ],
  });

  const terminals = terminalsQ.data ?? [];
  const sites = sitesQ.data ?? [];
  const terminalTypes = typesQ.data?.terminal_types ?? [];
  const capabilities = typesQ.data?.capabilities ?? [];
  const communicationModes = typesQ.data?.communication_modes ?? [];
  const unconfiguredTerminals = unconfiguredQ.data ?? [];
  const loading = terminalsQ.isPending;
  const loadingUnconfigured = unconfiguredQ.isPending;

  const stats = useMemo(() => ({
    total: terminals.length,
    online: terminals.filter((t) => t.status === "online").length,
    offline: terminals.filter((t) => t.status === "offline").length,
    error: terminals.filter((t) => t.status === "error").length,
  }), [terminals]);

  const loadTerminals = () => queryClient.invalidateQueries({ queryKey: ["terminals"] });
  const loadUnconfiguredTerminals = () => queryClient.invalidateQueries({ queryKey: ["unconfigured-terminals"] });


  const handleUseDetectedTerminal = (detectedTerminal) => {
    // Pré-remplir le formulaire avec les données du terminal détecté
    setNewTerminal({
      ...newTerminal,
      name: `Terminal ${detectedTerminal.serial_number}`,
      serial_number: detectedTerminal.serial_number,
      communication_mode: "push",
      ip_address: detectedTerminal.ip_address !== "unknown" ? detectedTerminal.ip_address : "",
      site_id: sites.length > 0 ? sites[0].id : "",
    });

    // Ouvrir le modal d'ajout directement à l'étape 3 (le terminal est déjà détecté)
    setWizardStep(3);
    setShowAddModal(true);

    toast.success(t("terminalMgmt.detected", { serial: detectedTerminal.serial_number }));
  };

  const handleAddTerminal = async () => {
    try {

      // Validate required fields
      if (!newTerminal.name || !newTerminal.site_id) {
        toast.error(t("terminalMgmt.requiredFields"));
        return;
      }

      if (newTerminal.communication_mode === "push" && !newTerminal.serial_number) {
        toast.error(t("terminalMgmt.serialRequired"));
        return;
      }

      if (newTerminal.communication_mode === "pull" && !newTerminal.ip_address) {
        toast.error(t("terminalMgmt.ipRequired"));
        return;
      }

      // Prevent simulation/test terminals
      const simulationPatterns = [
        /test/i,
        /demo/i,
        /simulation/i,
        /simulat/i,
        /fake/i,
        /debug/i,
        /dev/i,
        /sample/i,
        /exemple/i,
        /ZK001\d+/i, // Pattern like ZK001234
        /^TEST\d+$/i,
        /^DEMO\d+$/i,
      ];

      const isSimulationTerminal = simulationPatterns.some(
        (pattern) =>
          pattern.test(newTerminal.name) ||
          pattern.test(newTerminal.serial_number || "") ||
          (newTerminal.ip_address && pattern.test(newTerminal.ip_address))
      );

      if (isSimulationTerminal) {
        toast.error(t("terminalMgmt.simBlocked"));
        return;
      }


      await zktecoAPI.createTerminal(newTerminal);

      toast.success(t("terminalMgmt.created"));
      setShowAddModal(false);
      resetForm();
      loadTerminals();
      loadUnconfiguredTerminals(); // Actualiser la liste des terminaux non configurés
    } catch (error) {
      toast.error(error.response?.data?.detail || t("terminalMgmt.createError"));
    }
  };

  const resetForm = () => {
    setWizardStep(1);
    setNewTerminal({
      name: "",
      communication_mode: "push",
      terminal_type: "biometric",
      ip_address: "",
      port: 4370,
      serial_number: "",
      site_id: "",
      model: "",
      capabilities: [],
      firmware_version: "",
      max_users: null,
      max_logs: null,
    });
    setConnectionTestResult(null);
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionTestResult(null);

    try {
      if (newTerminal.communication_mode === "push") {
        // Test réel pour le mode Push/ADMS
        if (!newTerminal.serial_number) {
          toast.error(t("terminalMgmt.serialForTest"));
          setTestingConnection(false);
          return;
        }

        // Test ADMS réel via l'API
        const testData = {
          serial_number: newTerminal.serial_number,
          mode: "push",
          endpoint_url: `${window.location.protocol}//${window.location.hostname}/api/v1/zkteco/push`,
        };

        const response = await zktecoAPI.testADMSConnection(testData);

        if (response.data.success) {
          setConnectionTestResult({
            success: true,
            message: response.data.message || t("terminalMgmt.admsSuccess"),
            details: response.data.details || t("terminalMgmt.admsDetailFallback", { serial: newTerminal.serial_number }),
            tests: response.data.tests_performed || [],
          });
          toast.success(t("terminalMgmt.admsValidated"));
        } else {
          setConnectionTestResult({
            success: false,
            message: response.data.message || t("terminalMgmt.admsFailed"),
            details: response.data.details || t("terminalMgmt.admsFailDetail"),
            tests: response.data.tests_performed || [],
          });
          toast.error(t("terminalMgmt.admsFailedToast"));
        }
      } else {
        // Test pour le mode Pull
        if (!newTerminal.ip_address || !newTerminal.port) {
          toast.error(t("terminalMgmt.ipPortForTest"));
          setTestingConnection(false);
          return;
        }

        const testData = {
          ip_address: newTerminal.ip_address,
          port: newTerminal.port,
          timeout: 5000,
        };

        const response = await zktecoAPI.testConnection(testData);

        if (response.data.success) {
          setConnectionTestResult({
            success: true,
            message: t("terminalMgmt.connSuccess"),
            details: t("terminalMgmt.connDetail", { ip: newTerminal.ip_address, port: newTerminal.port }),
          });
          toast.success(t("terminalMgmt.connNetSuccess"));
        } else {
          setConnectionTestResult({
            success: false,
            message: t("terminalMgmt.connFailed"),
            details: response.data.details || t("terminalMgmt.connFailDetail"),
          });
          toast.error(t("terminalMgmt.connFailedToast"));
        }
      }
    } catch (error) {
      setConnectionTestResult({
        success: false,
        message: t("terminalMgmt.testError"),
        details: t("terminalMgmt.testErrorDetail"),
      });
      toast.error(t("terminalMgmt.testErrorToast"));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDeleteTerminal = async (terminalId) => {
    try {
      await zktecoAPI.deleteTerminal(terminalId);
      toast.success(t("terminalMgmt.deleted"));
      loadTerminals();
    } catch (error) {
      toast.error(t("terminalMgmt.deleteError"));
    }
  };

  const handleEditTerminal = async (terminal) => {
    try {
      setEditingTerminal(terminal);

      // Load fresh terminal data
      const response = await zktecoAPI.getTerminal(terminal.id);
      const terminalData = response.data;

      // Set form data for editing
      setEditFormData({
        name: terminalData.name || "",
        communication_mode: terminalData.communication_mode || "pull",
        terminal_type: terminalData.terminal_type || "biometric",
        ip_address: terminalData.ip_address || "",
        port: terminalData.port || 4370,
        serial_number: terminalData.serial_number || "",
        site_id: terminalData.site_id || "",
        model: terminalData.model || "",
        capabilities: terminalData.capabilities || [],
        firmware_version: terminalData.firmware_version || "",
        max_users: terminalData.max_users || null,
        max_logs: terminalData.max_logs || null,
        is_active: terminalData.is_active !== false,
      });

      setShowEditModal(true);
    } catch (error) {
      toast.error(t("terminalMgmt.loadError"));
    }
  };

  const handleUpdateTerminal = async () => {
    try {

      if (!editFormData.name || !editFormData.site_id) {
        toast.error(t("terminalMgmt.requiredFields"));
        return;
      }

      if (editFormData.communication_mode === "push" && !editFormData.serial_number) {
        toast.error(t("terminalMgmt.serialRequired"));
        return;
      }

      if (editFormData.communication_mode === "pull" && !editFormData.ip_address) {
        toast.error(t("terminalMgmt.ipRequired"));
        return;
      }

      // Prevent simulation/test terminals
      const simulationPatterns = [
        /test/i,
        /demo/i,
        /simulation/i,
        /simulat/i,
        /fake/i,
        /debug/i,
        /dev/i,
        /sample/i,
        /exemple/i,
        /ZK001\d+/i, // Pattern like ZK001234
        /^TEST\d+$/i,
        /^DEMO\d+$/i,
      ];

      const isSimulationTerminal = simulationPatterns.some(
        (pattern) =>
          pattern.test(editFormData.name) ||
          pattern.test(editFormData.serial_number || "") ||
          (editFormData.ip_address && pattern.test(editFormData.ip_address))
      );

      if (isSimulationTerminal) {
        toast.error(t("terminalMgmt.simBlocked"));
        return;
      }

      await zktecoAPI.updateTerminal(editingTerminal.id, editFormData);

      toast.success(t("terminalMgmt.updated"));
      setShowEditModal(false);
      resetEditForm();
      loadTerminals();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("terminalMgmt.updateError"));
    }
  };

  const resetEditForm = () => {
    setEditingTerminal(null);
    setEditFormData({});
  };

  const handleViewTerminalDetails = async (terminal) => {
    try {
      // Use the terminal data from the list directly (it's already synced and up-to-date)
      setSelectedTerminalDetails(terminal);
      setShowDetailsModal(true);
    } catch (error) {
      // Fallback to basic terminal data from the list
      setSelectedTerminalDetails(terminal);
      setShowDetailsModal(true);
    }
  };

  const openDetails = async (terminal) => {
    setSelectedTerminalDetails(terminal);
    setShowDetailsModal(true);
    try {
      // D'abord, lire les métriques issues du PUSH (source officielle ADMS)
      const pushRes = await zktecoAPI.getPushMetrics(terminal.id);
      if (pushRes.data?.success) {
        const m = pushRes.data.metrics || {};
        setDiag({
          device_info: {
            name: m.model,
            firmware: m.firmware,
            platform: m.platform || m.push_ver,
            users_count: m.users_count,
            users_max: m.max_users,
            attendance_count: m.attendance_count,
            attendance_max: m.max_logs,
            admin_count: m.admin_count,
            password_count: m.password_count,
            finger_count: m.finger_count,
            face_count: m.face_count,
            palm_count: m.palm_count,
            card_count: m.card_count,
          },
        });
      }
      // Demander au terminal d'envoyer ses métriques (INFO/OPTIONS) puis relire après 2s
      try {
        await zktecoAPI.refreshTerminalMetrics(terminal.id);
      } catch (_) {}
      setTimeout(async () => {
        try {
          const again = await zktecoAPI.getPushMetrics(terminal.id);
          if (again.data?.success) {
            const m2 = again.data.metrics || {};
            setDiag((prev) => ({
              device_info: {
                ...(prev?.device_info || {}),
                name: m2.model || prev?.device_info?.name,
                firmware: m2.firmware || prev?.device_info?.firmware,
                platform: m2.platform || m2.push_ver || prev?.device_info?.platform,
                users_count: typeof m2.users_count === "number" ? m2.users_count : prev?.device_info?.users_count,
                users_max: m2.max_users || prev?.device_info?.users_max,
                attendance_count:
                  typeof m2.attendance_count === "number" ? m2.attendance_count : prev?.device_info?.attendance_count,
                attendance_max: m2.max_logs || prev?.device_info?.attendance_max,
                admin_count: m2.admin_count ?? prev?.device_info?.admin_count,
                password_count: m2.password_count ?? prev?.device_info?.password_count,
                finger_count: m2.finger_count ?? prev?.device_info?.finger_count,
                face_count: m2.face_count ?? prev?.device_info?.face_count,
                palm_count: m2.palm_count ?? prev?.device_info?.palm_count,
                card_count: m2.card_count ?? prev?.device_info?.card_count,
              },
            }));
          }
        } catch (_) {}
      }, 2000);
      // En fallback (mode Pull/local), tenter le diagnostic direct
      if (!pushRes.data?.metrics && terminal.communication_mode !== "push") {
        const res = await zktecoAPI.getTerminalDiagnostic(terminal.id);
        if (res.data?.success) setDiag(res.data);
      }
    } catch (e) {
      setDiag(null);
    }
  };

  // Gestion de la sélection multiple
  const handleSelectTerminal = (terminalId) => {
    setSelectedTerminals((prev) => {
      if (prev.includes(terminalId)) {
        return prev.filter((id) => id !== terminalId);
      } else {
        return [...prev, terminalId];
      }
    });
  };

  const handleSelectAllTerminals = () => {
    if (selectedTerminals.length === filteredTerminals.length) {
      setSelectedTerminals([]);
    } else {
      setSelectedTerminals(filteredTerminals.map((terminal) => terminal.id));
    }
  };

  // Synchronisation du temps pour un terminal
  const handleSyncTerminalTime = async (terminalId) => {
    try {
      toast.loading(t("terminalMgmt.syncTimeLoading"), { id: "sync-time" });

      const response = await zktecoAPI.syncTerminalTime(terminalId);

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, { id: "sync-time" });
        // Recharger les terminaux pour mettre à jour les informations
        await loadTerminals();
      } else {
        toast.error(`❌ ${response.data.message}`, { id: "sync-time" });
      }
    } catch (error) {
      toast.error(t("terminalMgmt.syncTimeError"), { id: "sync-time" });
    }
  };

  // Synchronisation du temps pour plusieurs terminaux
  const handleSyncMultipleTerminalsTime = async () => {
    if (selectedTerminals.length === 0) {
      toast.error(t("terminalMgmt.selectAtLeastOne"));
      return;
    }

    try {
      toast.loading(t("terminalMgmt.syncTimeMultiLoading", { count: selectedTerminals.length }), {
        id: "sync-multiple-time",
      });

      const response = await zktecoAPI.syncMultipleTerminalsTime(selectedTerminals);

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, { id: "sync-multiple-time" });
        // Afficher les détails des résultats
        if (response.data.results) {
          const successCount = response.data.results.filter((r) => r.success).length;
          const failureCount = response.data.results.filter((r) => !r.success).length;

          if (failureCount > 0) {
            toast.info(t("terminalMgmt.resultsSummary", { success: successCount, failure: failureCount }), {
              id: "sync-results",
              duration: 5000,
            });
          }
        }
        // Recharger les terminaux
        await loadTerminals();
        // Vider la sélection
        setSelectedTerminals([]);
      } else {
        toast.error(`❌ ${response.data.message}`, { id: "sync-multiple-time" });
      }
    } catch (error) {
      toast.error(t("terminalMgmt.syncTimeError"), { id: "sync-multiple-time" });
    }
  };

  // Récupération des pointages pour plusieurs terminaux
  const handleFetchMultipleTerminalsAttendance = async () => {
    if (selectedTerminals.length === 0) {
      toast.error(t("terminalMgmt.selectAtLeastOne"));
      return;
    }

    try {
      toast.loading(t("terminalMgmt.fetchAttLoading", { count: selectedTerminals.length }), {
        id: "fetch-multiple-attendance",
      });

      const response = await zktecoAPI.fetchMultipleTerminalsAttendance(selectedTerminals);

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, { id: "fetch-multiple-attendance" });
        // Afficher les détails des résultats
        if (response.data.results) {
          const successCount = response.data.results.filter((r) => r.success).length;
          const failureCount = response.data.results.filter((r) => !r.success).length;
          const totalRecords = response.data.results.reduce((sum, r) => sum + (r.records_count || 0), 0);

          toast.info(
            t("terminalMgmt.fetchAttResults", { records: totalRecords, success: successCount, failure: failureCount }),
            {
              id: "fetch-results",
              duration: 8000,
            }
          );
        }
        // Recharger les terminaux
        await loadTerminals();
        // Vider la sélection
        setSelectedTerminals([]);
      } else {
        toast.error(`❌ ${response.data.message}`, { id: "fetch-multiple-attendance" });
      }
    } catch (error) {
      toast.error(t("terminalMgmt.fetchAttError"), { id: "fetch-multiple-attendance" });
    }
  };

  // Synchronisation des données utilisateur pour un terminal
  const handleSyncTerminalUsers = async (terminalId, syncTypes = ["USER", "FINGERTMP", "FaceTmp", "USERPIC"]) => {
    try {
      setSyncingUserData(true);
      toast.loading(t("terminalMgmt.syncUsersLoading"), { id: "sync-users" });

      const terminal = terminals.find((t) => t.id === terminalId);
      const response = await zktecoAPI.syncTerminalUsers(terminal.serial_number, {
        sync_types: syncTypes,
        auto_map: true,
      });

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, { id: "sync-users" });
        if (response.data.summary) {
          const summary = response.data.summary;
          toast.info(
            t("terminalMgmt.syncUsersSummary", { commands: summary.commands_sent || 0, types: (summary.sync_types || []).join(", ") }),
            { id: "sync-summary", duration: 5000 }
          );
        }
      } else {
        toast.error(`❌ ${response.data.message}`, { id: "sync-users" });
      }
    } catch (error) {
      toast.error(t("terminalMgmt.syncUsersError"), { id: "sync-users" });
    } finally {
      setSyncingUserData(false);
    }
  };

  // Synchronisation des données utilisateur pour plusieurs terminaux
  const handleSyncMultipleTerminalsUsers = async () => {
    if (selectedTerminals.length === 0) {
      toast.error(t("terminalMgmt.selectAtLeastOne"));
      return;
    }

    try {
      setSyncingUserData(true);
      toast.loading(t("terminalMgmt.syncUsersMultiLoading", { count: selectedTerminals.length }), {
        id: "sync-multiple-users",
      });

      // Synchroniser chaque terminal sélectionné individuellement
      const results = [];
      const syncRequest = {
        sync_types: ["USER", "FINGERTMP", "FaceTmp", "USERPIC"],
        auto_map: true,
      };

      for (const terminalId of selectedTerminals) {
        const terminal = terminals.find((t) => t.id === terminalId);
        if (terminal) {
          try {
            const response = await zktecoAPI.syncTerminalUsers(terminal.serial_number, syncRequest);
            results.push({
              success: true,
              terminal_name: terminal.name,
              serial_number: terminal.serial_number,
              message: t("terminalMgmt.syncSuccessMsg"),
            });
          } catch (error) {
            results.push({
              success: false,
              terminal_name: terminal.name,
              serial_number: terminal.serial_number,
              message: error.message || t("terminalMgmt.unknownError"),
            });
          }
        }
      }

      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.filter((r) => !r.success).length;

      toast.success(t("terminalMgmt.syncDone", { success: successCount, failure: failureCount }), {
        id: "sync-multiple-users",
      });

      // Afficher les détails des échecs s'il y en a
      const failures = results.filter((r) => !r.success);
      if (failures.length > 0) {
        failures.forEach((failure, index) => {
          setTimeout(() => {
            toast.error(t("terminalMgmt.syncErrorItem", { name: failure.terminal_name, message: failure.message }), {
              id: `sync-error-${index}`,
              duration: 5000,
            });
          }, index * 1000);
        });
      }

      // Vider la sélection
      setSelectedTerminals([]);
    } catch (error) {
      toast.error(t("terminalMgmt.syncUsersError"), { id: "sync-multiple-users" });
    } finally {
      setSyncingUserData(false);
    }
  };

  // Ouvrir le modal de synchronisation pour un terminal spécifique
  const openUserSyncModal = (terminal) => {
    setSelectedTerminalForSync(terminal);
    setShowUserSyncModal(true);
  };

  const filteredTerminals = terminals.filter((terminal) => {
    const matchesSearch =
      terminal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      terminal.serial_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || terminal.status === statusFilter;
    const matchesSite = siteFilter === "all" || terminal.site_id.toString() === siteFilter;
    const matchesMode = modeFilter === "all" || terminal.communication_mode === modeFilter;

    return matchesSearch && matchesStatus && matchesSite && matchesMode;
  });

  // URL serveur pour configuration des terminaux
  const serverUrl = `${window.location.protocol}//${window.location.hostname}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("terminalMgmt.title")}</h1>
          <p className="text-muted-foreground">{t("terminalMgmt.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => (window.location.href = "/biometric-mapping")}
            className="bg-purple-500 hover:bg-purple-600 border-purple-700"
          >
            <UserCheck className="h-4 w-4 mr-2" />
            {t("terminalMgmt.biometricMapping")}
          </Button>
          <AddTerminalWizard
            open={showAddModal}
            onOpenChange={setShowAddModal}
            wizardStep={wizardStep}
            setWizardStep={setWizardStep}
            newTerminal={newTerminal}
            setNewTerminal={setNewTerminal}
            sites={sites}
            terminalTypes={terminalTypes}
            unconfiguredTerminals={unconfiguredTerminals}
            loadingUnconfigured={loadingUnconfigured}
            loadUnconfiguredTerminals={loadUnconfiguredTerminals}
            handleUseDetectedTerminal={handleUseDetectedTerminal}
            handleAddTerminal={handleAddTerminal}
            resetForm={resetForm}
            serverUrl={serverUrl}
          />
        </div>
      </div>

      <EditTerminalModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        editingTerminal={editingTerminal}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        sites={sites}
        terminalTypes={terminalTypes}
        capabilities={capabilities}
        communicationModes={communicationModes}
        handleUpdateTerminal={handleUpdateTerminal}
      />

      <TerminalDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        terminal={selectedTerminalDetails}
        diag={diag}
        onEdit={handleEditTerminal}
        onRefresh={openDetails}
      />

      {/* Bannière configuration serveur */}
      <Card className="border-border">
        <CardContent className="py-4 px-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Server className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">{t("terminalMgmt.serverUrlLabel")}</p>
                <p className="text-xs text-muted-foreground">{t("terminalMgmt.serverMenuHint")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-gray-900 rounded px-3 py-1.5">
              <code className="text-green-400 font-mono text-sm">{serverUrl}</code>
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 text-xs"
                onClick={() => { navigator.clipboard.writeText(serverUrl); toast.success(t("terminalMgmt.urlCopied")); }}
              >
                <Copy className="h-3 w-3 mr-1" /> {t("terminalMgmt.copy")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <UnconfiguredTerminalsBanner
        unconfiguredTerminals={unconfiguredTerminals}
        loadingUnconfigured={loadingUnconfigured}
        loadUnconfiguredTerminals={loadUnconfiguredTerminals}
        handleUseDetectedTerminal={handleUseDetectedTerminal}
      />

      <TerminalStatsCards stats={stats} />

      <TerminalTableCard
        filteredTerminals={filteredTerminals}
        loading={loading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        modeFilter={modeFilter}
        setModeFilter={setModeFilter}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        selectedTerminals={selectedTerminals}
        setSelectedTerminals={setSelectedTerminals}
        handleSelectAllTerminals={handleSelectAllTerminals}
        handleSelectTerminal={handleSelectTerminal}
        handleSyncTerminalTime={handleSyncTerminalTime}
        handleSyncTerminalUsers={handleSyncTerminalUsers}
        handleFetchMultipleTerminalsAttendance={handleFetchMultipleTerminalsAttendance}
        handleSyncMultipleTerminalsTime={handleSyncMultipleTerminalsTime}
        handleSyncMultipleTerminalsUsers={handleSyncMultipleTerminalsUsers}
        syncingUserData={syncingUserData}
        openDetails={openDetails}
        handleEditTerminal={handleEditTerminal}
        handleDeleteTerminal={handleDeleteTerminal}
        loadTerminals={loadTerminals}
        loadUnconfiguredTerminals={loadUnconfiguredTerminals}
      />
    </div>
  );
};

export default TerminalManagement;
